import { getCurrentLanguage, initializeTranslations, applyTranslations, resolvePatchNoteSection } from './js/translations.js';

const FEED_PATH = '/data/patch-notes/website-feed.json';

const escapeHtml = (value) =>
  String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');

// Escape HTML, then turn inline `code` spans into <code> elements so authored
// plain text (e.g. domains like `@gmail.com`) renders safely.
const renderItem = (text) =>
  escapeHtml(text).replace(/`([^`]+)`/g, '<code>$1</code>');

let cachedEntries = [];

const renderStatus = (container, key, fallback) => {
  container.innerHTML = `<p class="updates-status" data-translate="${key}">${fallback}</p>`;
  applyTranslations('updates', getCurrentLanguage());
};

const renderMediaGroups = (mediaGroups, mediaLegends = []) => {
  if (!mediaGroups?.length) return '';

  return mediaGroups.map((group, groupIndex) => {
    const legendGroup = mediaLegends[groupIndex] ?? [];
    const images = group.map((image, imageIndex) => {
      const src = escapeHtml(image.src);
      const legend = escapeHtml(legendGroup[imageIndex] ?? '');
      const caption = legend
        ? `<figcaption class="updates-media-legend">${legend}</figcaption>`
        : '';
      return `<figure class="updates-media-item">${caption}<img class="updates-media-image" src="${src}" alt="${legend}" loading="lazy" /></figure>`;
    }).join('');
    return `<div class="updates-media-group">${images}</div>`;
  }).join('');
};

const renderSection = (slug, section, lang) => {
  const resolved = resolvePatchNoteSection(slug, section.id, lang);
  if (!resolved || resolved.items.length === 0) {
    return '';
  }

  const title = escapeHtml(resolved.title);
  const items = resolved.items.map((item) => `<li>${renderItem(item)}</li>`).join('');
  const media = renderMediaGroups(section.mediaGroups, resolved.mediaLegends);
  return `<h2>${title}</h2><ul>${items}</ul>${media}`;
};

const renderEntries = (container, entries, lang) => {
  container.innerHTML = entries
    .map((entry) => {
      const dateLabel = escapeHtml(entry.dateLabel || entry.dateIso || 'Update');
      const slug = entry.slug || 'website-update';
      const slugAttr = escapeHtml(slug);
      const sections = Array.isArray(entry.sections) ? entry.sections : [];
      const body = sections.map((section) => renderSection(slug, section, lang)).join('');

      return `
        <article class="updates-entry" aria-labelledby="${slugAttr}-title">
          <h2 id="${slugAttr}-title" class="updates-entry-title">${dateLabel}</h2>
          <div class="updates-entry-body">${body}</div>
        </article>
      `;
    })
    .join('');
};

const initUpdatesPage = async () => {
  const updatesList = document.getElementById('updates-list');
  if (!updatesList) {
    return;
  }

  await initializeTranslations('updates');

  try {
    const response = await fetch(FEED_PATH, { cache: 'no-store' });
    if (!response.ok) {
      throw new Error(`Failed with status ${response.status}`);
    }

    const payload = await response.json();
    cachedEntries = Array.isArray(payload?.entries) ? payload.entries : [];

    if (cachedEntries.length === 0) {
      renderStatus(updatesList, 'updates.empty', 'No updates available yet.');
      return;
    }

    renderEntries(updatesList, cachedEntries, getCurrentLanguage() || 'en');
  } catch (error) {
    console.error('[Updates] Failed to load website feed:', error);
    renderStatus(updatesList, 'updates.loadError', 'Unable to load updates right now.');
  }
};

document.addEventListener('languageSwitched', (event) => {
  if (event.detail?.page !== 'updates') {
    return;
  }
  const updatesList = document.getElementById('updates-list');
  if (!updatesList || cachedEntries.length === 0) {
    return;
  }
  renderEntries(updatesList, cachedEntries, event.detail.language || getCurrentLanguage() || 'en');
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initUpdatesPage);
} else {
  initUpdatesPage();
}
